import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '../Components/AdminSidebar';
import { API_BASE_URL, API_SYNC_BASE_URL } from '../config';
import '../Styles/AdminPage.css';

const Admin = () => {
    const [stats, setStats] = useState({
        users: 0,
        products: 0,
        orders: 0,
        categories: 0,
        revenue: 0
    });
    const [admins, setAdmins] = useState([]);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newUsername: '',
        newPassword: ''
    });
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [tempUsername, setTempUsername] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [seasons, setSeasons] = useState([]);
    const [loadingSeasons, setLoadingSeasons] = useState(false);
    const [showSeasonModal, setShowSeasonModal] = useState(false);
    const [runningApiSync, setRunningApiSync] = useState(false);
    const [showApiSyncDialog, setShowApiSyncDialog] = useState(false);
    const [apiSyncStatus, setApiSyncStatus] = useState({ success: false, message: '' });

    useEffect(() => {
        fetchStats();
        fetchAdmins();
        fetchSeasons();
    }, []);

    const fetchStats = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/stats`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setStats(res.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchAdmins = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/admins`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setAdmins(res.data);
        } catch (err) {
            console.error('Error fetching admins:', err);
        }
    };

    const fetchSeasons = async () => {
        setLoadingSeasons(true);
        const adminToken = localStorage.getItem('adminToken');
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/seasons`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setSeasons(res.data);
        } catch (err) {
            console.error('Error fetching seasons:', err);
        } finally {
            setLoadingSeasons(false);
        }
    };

    const handleSeasonToggle = async (season) => {
        const adminToken = localStorage.getItem('adminToken');
        const newSelected = season.selected ? 0 : 1;
        try {
            await axios.put(`${API_BASE_URL}/admin/seasons/${season.id}`, {
                ...season,
                selected: newSelected
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            const action = newSelected ? 'activated' : 'deactivated';
            setMessage({ type: 'success', text: `Season "${season.name} ${season.year}" ${action}` });
            fetchSeasons();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update season' });
        }
    };

    const handleAccountAction = async (e) => {
        e.preventDefault();
        const adminToken = localStorage.getItem('adminToken');

        try {
            if (admins.length === 0) {
                // CREATE for the first time
                await axios.post(`${API_BASE_URL}/admin/admins`, {
                    username: passwordData.newUsername,
                    password: passwordData.newPassword
                });
                setMessage({ type: 'success', text: 'Master Account created successfully' });
            } else {
                // UPDATE existing (Password Only)
                await axios.put(`${API_BASE_URL}/admin/reset-password`, {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                }, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
                setMessage({ type: 'success', text: 'Password updated successfully' });
            }
            setPasswordData({ currentPassword: '', newUsername: '', newPassword: '' });
            fetchAdmins();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Action failed' });
        }
    };

    const handleUsernameUpdate = async (adminId) => {
        const adminToken = localStorage.getItem('adminToken');
        if (!tempUsername.trim()) return;

        try {
            await axios.put(`${API_BASE_URL}/admin/admins/${adminId}`, {
                username: tempUsername
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setMessage({ type: 'success', text: 'Username updated successfully' });
            setIsEditingUsername(false);
            fetchAdmins();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update username' });
        }
    };

    const handleRunApiSync = async () => {
        const adminToken = localStorage.getItem('adminToken');
        setRunningApiSync(true);
        setMessage({ type: '', text: '' });

        try {
            const res = await axios.post(`${API_SYNC_BASE_URL}/sync/run`, {}, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setApiSyncStatus({ success: true, message: res.data.message || 'API sync completed successfully!' });
            setShowApiSyncDialog(true);
        } catch (err) {
            console.error('API Sync Error:', {
                status: err.response?.status,
                message: err.response?.data?.message,
                error: err.message,
                url: `${API_SYNC_BASE_URL}/sync/run`
            });
            setApiSyncStatus({ success: false, message: err.response?.data?.message || 'Failed to run API sync' });
            setShowApiSyncDialog(true);
        } finally {
            setRunningApiSync(false);
        }
    };

    return (
        <div className="admin-page-container">
            <AdminSidebar />
            <div className="admin-main-content">
                {message.text && (
                    <div className={`admin-alert ${message.type}`}>
                        {message.text}
                        <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
                    </div>
                )}

                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon users">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.users}</h3>
                            <p>Total Users</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon products">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.products}</h3>
                            <p>Products</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon orders">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.orders}</h3>
                            <p>Orders</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon revenue">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                        </div>
                        <div className="stat-info">
                            <h3>${parseFloat(stats.revenue || 0).toFixed(2)}</h3>
                            <p>Revenue</p>
                        </div>
                    </div>
                </div>

                <div className="admin-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>Dashboard Overview</h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button
                            className="change-season-btn"
                            onClick={() => setShowSeasonModal(true)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
                            Active Seasons: {seasons.filter(s => s.selected).map(s => s.name).join(', ') || 'None'}
                        </button>
                        <button
                            className="api-sync-btn"
                            onClick={handleRunApiSync}
                            disabled={runningApiSync}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M3 21v-5h5"></path></svg>
                            {runningApiSync ? 'Running API...' : 'API'}
                        </button>
                    </div>
                </div>

                {showSeasonModal && (
                    <div className="modal-overlay">
                        <div className="admin-card season-modal-content" style={{ maxWidth: '600px', width: '90%', maxHeight: '85vh' }}>
                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2>Manage Active Seasons</h2>
                                <button className="close-modal-btn" onClick={() => setShowSeasonModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
                            </div>
                            <div className="admin-list season-selection-list">
                                {loadingSeasons ? (
                                    <div className="loading-placeholder">Loading seasons...</div>
                                ) : seasons.length === 0 ? (
                                    <div className="empty-state">No seasons defined. Go to Definitions to add one.</div>
                                ) : (
                                    <div className="seasons-list-rows">
                                        {seasons.map(season => (
                                            <div
                                                key={season.id}
                                                className={`season-row-item ${season.selected ? 'active' : ''}`}
                                            >
                                                <div className="season-row-main">
                                                    <div className="season-row-icon">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
                                                    </div>
                                                    <div className="season-row-details">
                                                        <span className="season-row-name">{season.name}</span>
                                                        <span className="season-row-year">{season.year} Collection</span>
                                                    </div>
                                                </div>
                                                <div className="season-row-actions">
                                                    {season.selected ? (
                                                        <button
                                                            className="deactivate-row-btn"
                                                            onClick={() => handleSeasonToggle(season)}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                            Active
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="activate-row-btn"
                                                            onClick={() => handleSeasonToggle(season)}
                                                        >
                                                            Activate
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '6px' }}><polyline points="9 18 15 12 9 6"></polyline></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '20px 30px', borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
                                <button className="admin-cancel-btn" onClick={() => setShowSeasonModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {showApiSyncDialog && (
                    <div className="modal-overlay">
                        <div className={`api-sync-dialog ${apiSyncStatus.success ? 'success' : 'error'}`}>
                            <div className="api-sync-icon">
                                {apiSyncStatus.success ? (
                                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                ) : (
                                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                    </svg>
                                )}
                            </div>
                            <h2 className="api-sync-title">
                                {apiSyncStatus.success ? 'Sync Completed' : 'Sync Failed'}
                            </h2>
                            <p className="api-sync-message">
                                {apiSyncStatus.message}
                            </p>
                            <button
                                className="api-sync-btn-primary"
                                onClick={() => setShowApiSyncDialog(false)}
                            >
                                {apiSyncStatus.success ? 'Great!' : 'Close'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="admin-grid-layout">
                    {/* Account Setup/Update Section */}
                    <div className="admin-card">
                        <div className="card-header">
                            <h2>{admins.length === 0 ? 'Initial Setup' : 'Update Password'}</h2>
                        </div>
                        <form onSubmit={handleAccountAction} className="admin-form">
                            {admins.length > 0 && (
                                <div className="form-group">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                        placeholder="Enter current password"
                                        required
                                    />
                                </div>
                            )}
                            {admins.length === 0 && (
                                <div className="form-group">
                                    <label>Master Username</label>
                                    <input
                                        type="text"
                                        value={passwordData.newUsername}
                                        onChange={(e) => setPasswordData({ ...passwordData, newUsername: e.target.value })}
                                        placeholder="e.g. admin"
                                        required
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label>{admins.length === 0 ? 'Master Password' : 'New Password'}</label>
                                <input
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    placeholder={admins.length === 0 ? 'e.g. secret123' : 'Enter new password'}
                                    required
                                />
                            </div>
                            <button type="submit" className="submit-btn">
                                {admins.length === 0 ? 'Create Master Account' : 'Update Password'}
                            </button>
                        </form>
                    </div>

                    {/* Master Account Info Section */}
                    <div className="admin-card">
                        <div className="card-header">
                            <h2>Account Status</h2>
                        </div>
                        <div className="admin-list">
                            {admins.length === 0 ? (
                                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                    <div style={{ color: '#ef4444', marginBottom: '15px' }}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                                    </div>
                                    <h3 style={{ margin: '0 0 10px', color: '#111' }}>No Admin Account</h3>
                                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Please use the form to create your first master administrator account.</p>
                                </div>
                            ) : (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Current Username</th>
                                            <th>Created At</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {admins.map(admin => (
                                            <tr key={admin.id}>
                                                <td style={{ fontWeight: 700 }}>
                                                    {isEditingUsername ? (
                                                        <input
                                                            type="text"
                                                            className="admin-inline-input"
                                                            value={tempUsername}
                                                            onChange={(e) => setTempUsername(e.target.value)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        admin.username
                                                    )}
                                                </td>
                                                <td>{admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}</td>
                                                <td>
                                                    {isEditingUsername ? (
                                                        <div className="admin-action-btns">
                                                            <button
                                                                className="admin-save-btn"
                                                                onClick={() => handleUsernameUpdate(admin.id)}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                className="admin-cancel-btn"
                                                                onClick={() => setIsEditingUsername(false)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className="admin-edit-btn"
                                                            onClick={() => {
                                                                setTempUsername(admin.username);
                                                                setIsEditingUsername(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="add-admin-footer" style={{ background: '#fff', border: 'none', textAlign: 'center', color: '#888' }}>
                            <p style={{ fontSize: '0.85rem' }}>
                                {admins.length === 0 ? 'Initial setup required.' : 'This dashboard is restricted to one master administrator account.'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Admin;
